import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import {
  getImageModelConfig,
  type ImageModelConfig,
  type ImageModelConfigPatch,
  type ImageModelEntry,
  updateImageModelConfig,
} from "@/api/admin-api"
import { ImagePlus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"

import { CollapsibleManagementSection } from "./collapsible-management-section"
import { adminQueryKeys } from "../hooks/admin-query-keys"

/**
 * Stands in for the stored empty string: Radix rejects `""` as a
 * `SelectItem` value, and "unset" has to be pickable so an admin can hand a
 * surface back to automatic resolution.
 */
const AUTOMATIC_VALUE = "__automatic__"

function ImageModelSelect({
  id,
  label,
  description,
  value,
  models,
  automaticLabel,
  disabledSuffix,
  onValueChange,
}: {
  id: string
  label: string
  description: string
  value: string
  models: ImageModelEntry[]
  automaticLabel: string
  disabledSuffix: string
  onValueChange: (value: string) => void
}) {
  // A stored id whose entry was deleted must stay visible, or saving any
  // other field would silently rewrite it.
  const orphaned = Boolean(value) && !models.some((model) => model.id === value)

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <p className="text-muted-foreground text-xs">{description}</p>
      <Select value={value || AUTOMATIC_VALUE} onValueChange={onValueChange}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AUTOMATIC_VALUE}>{automaticLabel}</SelectItem>
          {orphaned && <SelectItem value={value}>{value}</SelectItem>}
          {models.map((model) => (
            <SelectItem key={model.id} disabled={!model.enabled} value={model.id}>
              <span>
                {model.display_name}
                {model.enabled ? "" : disabledSuffix}
              </span>
              <span className="text-muted-foreground ml-2 text-xs">{model.family_label}</span>
              {/* The Azure deployment is what an admin matches against the
                  portal, so it has to be visible next to the family. */}
              <span className="text-muted-foreground ml-2 font-mono text-xs">
                {model.deployment}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * Admin controls for the three image surface keys (#3038): which registry
 * entry Explorer generates with, which one AI Studio defaults to, and whether
 * Studio users may override that default themselves.
 *
 * Each control saves on change — there are three independent switches, so a
 * staged "Save changes" step (as Tender needs for five coupled model slots)
 * would only add a click. Unset ids resolve server-side; `effective` carries
 * what they resolve to today and is shown as the "Automatic" option's label.
 */
export function ImageModelConfigPanel() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  // Overlays the server value between `mutate` and the response, so the
  // control the admin just moved does not flick back to its old value.
  const [pending, setPending] = useState<ImageModelConfigPatch>({})

  const {
    data: config,
    isLoading,
    isError,
  } = useQuery({
    queryKey: adminQueryKeys.imageModels(),
    queryFn: getImageModelConfig,
    staleTime: 60_000,
  })

  const saveMutation = useMutation({
    mutationFn: updateImageModelConfig,
    onSuccess: (result) => {
      // The PUT echoes the full GET shape — surface keys, effective, models
      // and families — so its response seeds the cache directly instead of
      // costing another round trip.
      queryClient.setQueryData<ImageModelConfig>(adminQueryKeys.imageModels(), result)
      setPending({})
      toast.success(
        t("adminDashboard.imageGeneration.saved", "Image generation configuration saved")
      )
    },
    onError: () => {
      setPending({})
      toast.error(
        t(
          "adminDashboard.imageGeneration.saveError",
          "Failed to update image generation configuration"
        )
      )
    },
  })

  const models = config?.models ?? []

  function save(patch: ImageModelConfigPatch) {
    setPending((current) => ({ ...current, ...patch }))
    saveMutation.mutate(patch)
  }

  function currentModelId(key: "explorer_model_id" | "studio_default_model_id"): string {
    return pending[key] ?? config?.[key] ?? ""
  }

  function automaticLabel(key: "explorer_model_id" | "studio_default_model_id"): string {
    const effectiveId = config?.effective[key] ?? ""
    const effective = models.find((model) => model.id === effectiveId)
    return t("adminDashboard.imageGeneration.automatic", {
      defaultValue: "Automatic: {{model}}",
      model: effective?.display_name ?? effectiveId,
    })
  }

  const userSelectable = pending.studio_user_selectable ?? config?.studio_user_selectable ?? true
  const disabledSuffix = t("adminDashboard.imageGeneration.disabledSuffix", " (disabled)")

  // The Management workspace renders one section at a time, so the Models
  // card is not in the DOM to scroll to — the empty-state CTA has to change
  // the route the same way the section picker does.
  function goToModelsSection() {
    const params = new URLSearchParams(searchParams)
    params.set("area", "configuration")
    params.set("setting", "models")
    params.delete("view")
    setSearchParams(params)
  }

  function renderBody() {
    if (isLoading) {
      return (
        <div className="text-muted-foreground py-6 text-center text-sm">
          {t("common.loading", "Loading...")}
        </div>
      )
    }

    if (isError) {
      return (
        <div className="text-destructive py-6 text-center text-sm">
          {t(
            "adminDashboard.imageGeneration.loadError",
            "Could not load the image generation configuration."
          )}
        </div>
      )
    }

    if (models.length === 0) {
      return (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <span className="bg-muted rounded-lg p-3">
            <ImagePlus aria-hidden="true" className="text-muted-foreground size-6" />
          </span>
          <h3 className="text-sm font-semibold">
            {t("adminDashboard.imageGeneration.emptyTitle", "No image models yet")}
          </h3>
          <p className="text-muted-foreground max-w-md text-sm">
            {t(
              "adminDashboard.imageGeneration.empty",
              "Add an image model under Models → Specialized first, then choose which one Explorer and AI Studio use."
            )}
          </p>
          <Button size="sm" variant="outline" onClick={goToModelsSection}>
            {t("adminDashboard.imageGeneration.emptyAction", "Go to Models")}
          </Button>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.imageGeneration.description",
            "Choose which image model each surface uses. Leave a surface on automatic to let the platform pick the best enabled model."
          )}
        </p>

        <div className="grid gap-6 md:grid-cols-2">
          <ImageModelSelect
            automaticLabel={automaticLabel("explorer_model_id")}
            disabledSuffix={disabledSuffix}
            id="image-explorer-model"
            label={t("adminDashboard.imageGeneration.explorerModel", "Explorer model")}
            models={models}
            value={currentModelId("explorer_model_id")}
            description={t(
              "adminDashboard.imageGeneration.explorerModelDescription",
              "Used by the image tool in Explorer, Deep Explorer, Scholar and voice mode."
            )}
            onValueChange={(value) =>
              save({ explorer_model_id: value === AUTOMATIC_VALUE ? "" : value })
            }
          />
          <ImageModelSelect
            automaticLabel={automaticLabel("studio_default_model_id")}
            disabledSuffix={disabledSuffix}
            id="image-studio-default-model"
            label={t("adminDashboard.imageGeneration.studioDefaultModel", "Studio default model")}
            models={models}
            value={currentModelId("studio_default_model_id")}
            description={t(
              "adminDashboard.imageGeneration.studioDefaultModelDescription",
              "The model AI Studio starts from when a user has not chosen one."
            )}
            onValueChange={(value) =>
              save({ studio_default_model_id: value === AUTOMATIC_VALUE ? "" : value })
            }
          />
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="image-studio-user-selectable">
              {t("adminDashboard.imageGeneration.userSelectable", "Let Studio users choose model")}
            </Label>
            <p className="text-muted-foreground text-xs">
              {t(
                "adminDashboard.imageGeneration.userSelectableHelp",
                "When off, AI Studio always uses the default model and hides the picker."
              )}
            </p>
          </div>
          <Switch
            checked={userSelectable}
            id="image-studio-user-selectable"
            onCheckedChange={(checked) => save({ studio_user_selectable: checked })}
          />
        </div>
      </div>
    )
  }

  return (
    <CollapsibleManagementSection
      icon={<ImagePlus className="size-5" />}
      id="image-generation"
      storageKey="admin-management-image-generation-expanded"
      title={t("adminDashboard.imageGeneration.title", "Image generation")}
    >
      {renderBody()}
    </CollapsibleManagementSection>
  )
}
